import React, { useEffect, useMemo, useRef, useState } from 'react';
import { API_URL } from '../../services/api';
import { getUserToken } from '../../context/AuthContext';
import { trackEvent, trackPurchaseOnce, GaItem } from '../../utils/analytics';
import { Smartphone, CheckCircle, Loader, Link2 } from 'lucide-react';
import Button from '../ui/Button';

interface PaymentGatewayProps {
    cvId?: string;
    letterId?: string;
    documentType?: 'cv' | 'letter';
    amount: number;
    /** Pour le tableau "items" GA4 (rapports Monétisation) */
    templateId?: string;
    templateName?: string;
    onClose: () => void;
    onSuccess: () => void;
    onSave?: () => Promise<string | null>;
}

type PayMethod = 'paylink' | 'wave' | 'mtn' | 'orange' | 'moov';

const ALL_METHODS: { id: PayMethod; name: string; icon: React.ReactNode; needsPhone: boolean }[] = [
    { id: 'paylink', name: 'Lien Nafolo', icon: <Link2 className="text-pink-600" />, needsPhone: false },
    { id: 'wave', name: 'Wave', icon: <Smartphone className="text-blue-500" />, needsPhone: true },
    { id: 'mtn', name: 'MTN Money', icon: <Smartphone className="text-yellow-500" />, needsPhone: true },
    { id: 'orange', name: 'Orange Money', icon: <Smartphone className="text-orange-500" />, needsPhone: true },
    { id: 'moov', name: 'Moov Money', icon: <Smartphone className="text-purple-500" />, needsPhone: true },
];

const PaymentGateway: React.FC<PaymentGatewayProps> = ({
    cvId,
    letterId,
    documentType = letterId ? 'letter' : 'cv',
    amount,
    templateId,
    templateName,
    onClose,
    onSuccess,
    onSave,
}) => {
    const isLetter = documentType === 'letter' || Boolean(letterId && !cvId);
    const documentId = isLetter ? letterId : cvId;
    const docLabel = isLetter ? 'lettre' : 'CV';

    // value part toujours en entier : XOF n'a pas de décimales, aucun
    // toFixed/parseFloat ne doit s'appliquer avant l'envoi à GA4.
    const gaItems = (): GaItem[] => [
        {
            item_id: templateId || '',
            item_name: templateName || '',
            item_category: isLetter ? 'letter' : 'cv',
            price: Math.round(amount),
            quantity: 1,
        },
    ];

    const [selectedMethod, setSelectedMethod] = useState<PayMethod | null>(null);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [paymentSuccess, setPaymentSuccess] = useState(false);
    const [enabledMap, setEnabledMap] = useState<Record<string, boolean> | null>(null);
    // Transaction en cours (initiée, pas encore résolue) — sert à distinguer un
    // abandon (fermeture manuelle) d'une simple fermeture du modal jamais commencée.
    const pendingTransactionRef = useRef<string | null>(null);

    useEffect(() => {
        fetch(`${API_URL}/payment/methods`)
            .then((r) => r.json())
            .then((data) => {
                const methods = data?.methods || {};
                setEnabledMap(methods);
            })
            .catch(() => {
                setEnabledMap({
                    paylink: true,
                    wave: true,
                    mtn: true,
                    orange: true,
                    moov: true,
                });
            });
    }, []);

    const methods = useMemo(() => {
        if (!enabledMap) return ALL_METHODS;
        return ALL_METHODS.filter((m) => enabledMap[m.id] !== false);
    }, [enabledMap]);

    useEffect(() => {
        if (!methods.length) {
            setSelectedMethod(null);
            return;
        }
        setSelectedMethod((prev) => {
            if (prev && methods.some((m) => m.id === prev)) return prev;
            return methods[0].id;
        });
    }, [methods]);

    const selected = methods.find((m) => m.id === selectedMethod);
    const needsPhone = selected?.needsPhone ?? false;

    const pollPaymentStatus = async (transactionId: string) => {
        const token = getUserToken();
        const interval = setInterval(async () => {
            try {
                const response = await fetch(`${API_URL}/payment/verify/${transactionId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (response.ok) {
                    const data = await response.json();
                    if (data.status === 'completed') {
                        clearInterval(interval);
                        pendingTransactionRef.current = null;
                        trackPurchaseOnce(transactionId, {
                            document_type: isLetter ? 'letter' : 'cv',
                            payment_method: selectedMethod || '',
                            value: Math.round(amount),
                            currency: 'XOF',
                            items: gaItems(),
                        });
                        setPaymentSuccess(true);
                        setTimeout(() => onSuccess(), 1500);
                    } else if (data.status === 'failed') {
                        clearInterval(interval);
                        pendingTransactionRef.current = null;
                        trackEvent('checkout_abandoned', {
                            reason: 'failed',
                            document_type: isLetter ? 'letter' : 'cv',
                            payment_method: selectedMethod || '',
                        });
                        setIsProcessing(false);
                        alert('Le paiement a échoué.');
                    }
                }
            } catch (e) {
                console.error('Polling error', e);
            }
        }, 3000);

        setTimeout(() => {
            clearInterval(interval);
            setIsProcessing((processing) => {
                if (processing) {
                    if (pendingTransactionRef.current === transactionId) {
                        pendingTransactionRef.current = null;
                        trackEvent('checkout_abandoned', {
                            reason: 'timeout',
                            document_type: isLetter ? 'letter' : 'cv',
                            payment_method: selectedMethod || '',
                        });
                    }
                    alert(
                        "Délai d'attente dépassé. Si vous avez payé, fermez cette fenêtre puis recliquez sur Commander — le paiement sera vérifié automatiquement."
                    );
                }
                return false;
            });
        }, 300000);
    };

    const handlePayment = async () => {
        if (!selectedMethod) return;
        if (needsPhone && !phoneNumber) return;

        trackEvent('begin_checkout', {
            document_type: isLetter ? 'letter' : 'cv',
            payment_method: selectedMethod,
            value: Math.round(amount),
            currency: 'XOF',
            items: gaItems(),
        });

        setIsProcessing(true);
        try {
            const token = getUserToken();
            const response = await fetch(`${API_URL}/payment/initiate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    cvId: isLetter ? undefined : cvId,
                    letterId: isLetter ? letterId : undefined,
                    documentType: isLetter ? 'letter' : 'cv',
                    method: selectedMethod,
                    phoneNumber: needsPhone ? phoneNumber : undefined,
                    amount,
                    redirectUrl: window.location.origin,
                }),
            });

            if (!response.ok) {
                let errorMessage = "Échec de l'initialisation du paiement";
                try {
                    const errorData = await response.json();
                    if (errorData?.error && !String(errorData.error).includes('<!DOCTYPE')) {
                        errorMessage = errorData.error;
                    }
                } catch {
                    /* ignore */
                }
                throw new Error(errorMessage);
            }

            const result = await response.json();

            if (result.redirectUrl) {
                window.open(result.redirectUrl, '_blank');
                if (result.payment?.transactionId) {
                    pendingTransactionRef.current = result.payment.transactionId;
                    pollPaymentStatus(result.payment.transactionId);
                }
            } else if (result.payment?.transactionId) {
                if (result.instructions) {
                    alert(result.instructions);
                } else {
                    alert(
                        'Paiement initié. Validez sur votre téléphone (notification Wave/MTN/Orange/Moov), puis attendez la confirmation.'
                    );
                }
                pendingTransactionRef.current = result.payment.transactionId;
                pollPaymentStatus(result.payment.transactionId);
            } else {
                throw new Error('Réponse de paiement invalide');
            }
        } catch (error: unknown) {
            console.error('Payment error:', error);
            const errorMessage =
                error instanceof Error ? error.message : 'Erreur lors du paiement. Veuillez réessayer.';
            alert(errorMessage);
            setIsProcessing(false);
        }
    };

    const handleClose = () => {
        if (pendingTransactionRef.current && !paymentSuccess) {
            trackEvent('checkout_abandoned', {
                reason: 'closed',
                document_type: isLetter ? 'letter' : 'cv',
                payment_method: selectedMethod || '',
            });
            pendingTransactionRef.current = null;
        }
        onClose();
    };

    if (paymentSuccess) {
        return (
            <div className="max-w-2xl mx-auto p-8 bg-white rounded-2xl shadow-lg border border-gray-100 text-center relative">
                <button
                    onClick={handleClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold mb-2">Paiement réussi !</h2>
                <p className="text-gray-600">
                    Votre {docLabel} sera téléchargé automatiquement...
                </p>
            </div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto p-8 bg-white rounded-2xl shadow-lg border border-gray-100 relative">
            <button
                onClick={handleClose}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>
            <h2 className="text-2xl font-bold text-center mb-2">
                {isLetter ? 'Finaliser ma lettre' : 'Finaliser mon CV'}
            </h2>

            {!documentId ? (
                <div className="mt-8 text-center p-6 bg-amber-50 rounded-2xl border border-amber-200">
                    <CheckCircle className="w-12 h-12 text-amber-500 mx-auto mb-4 opacity-50" />
                    <h3 className="text-lg font-bold text-amber-900 mb-2">Action requise</h3>
                    <p className="text-amber-700 text-sm mb-6">
                        Veuillez enregistrer votre {docLabel} avant de passer commande.
                    </p>
                    <Button
                        onClick={async () => {
                            if (onSave) await onSave();
                            else onClose();
                        }}
                        variant="primary"
                        className="w-full"
                    >
                        Enregistrer et voir le paiement
                    </Button>
                </div>
            ) : (
                <>
                    <p className="text-gray-500 text-center mb-8">
                        Choisissez votre mode de paiement pour débloquer le téléchargement
                    </p>

                    <div className="bg-indigo-50 p-4 rounded-xl mb-8 flex justify-between items-center">
                        <span className="text-indigo-900 font-medium">Prix à payer pour télécharger</span>
                        <span className="text-2xl font-bold text-indigo-700">{amount} FCFA</span>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
                        {methods.length === 0 && (
                            <p className="col-span-full text-center text-sm text-amber-700 bg-amber-50 rounded-xl p-4">
                                Aucun moyen de paiement n’est activé. Contactez le support.
                            </p>
                        )}
                        {methods.map((method) => (
                            <button
                                key={method.id}
                                type="button"
                                onClick={() => setSelectedMethod(method.id)}
                                disabled={isProcessing}
                                className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                                    selectedMethod === method.id
                                        ? 'border-indigo-600 bg-indigo-50'
                                        : 'border-gray-100 hover:border-indigo-200'
                                } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                {method.icon}
                                <span className="font-semibold text-sm text-center">{method.name}</span>
                                {selectedMethod === method.id && (
                                    <CheckCircle className="text-indigo-600 w-4 h-4" />
                                )}
                            </button>
                        ))}
                    </div>

                    {needsPhone && (
                        <div className="mb-6">
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Numéro de téléphone (10 chiffres)
                            </label>
                            <input
                                type="tel"
                                placeholder="Ex: 0701020304"
                                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                                value={phoneNumber}
                                onChange={(e) => setPhoneNumber(e.target.value)}
                            />
                        </div>
                    )}

                    {!needsPhone && (
                        <p className="text-sm text-slate-500 mb-6 text-center">
                            Un lien sécurisé Nafolo s&apos;ouvrira pour finaliser le paiement (Wave, MTN,
                            Orange, Moov, carte…).
                        </p>
                    )}

                    <Button
                        variant="primary"
                        className="w-full py-4 text-lg"
                        disabled={!selectedMethod || (needsPhone && !phoneNumber) || isProcessing}
                        onClick={handlePayment}
                    >
                        {isProcessing ? (
                            <>
                                <Loader className="w-5 h-5 mr-2 animate-spin" />
                                Traitement en cours...
                            </>
                        ) : (
                            `Payer ${amount} FCFA`
                        )}
                    </Button>

                    <p className="text-xs text-center text-gray-400 mt-6">
                        Paiement sécurisé via Nafolo · {amount} XOF
                    </p>
                </>
            )}
        </div>
    );
};

export default PaymentGateway;
